
import { useState, useEffect } from 'react';
import { usePermissions } from '@/contexts/unified-permissions';
import { toast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { isIOSNativeApp } from '@/utils/platformUtils';
import { handleIOSIAPError } from '@/utils/iosIAPErrorHandler';

// Import the components with named imports
import { SubscriptionHeader } from '@/components/subscription/SubscriptionHeader';
import { BillingPeriodSelector } from '@/components/subscription/BillingPeriodSelector';
import { SubscriptionPlans } from '@/components/subscription/SubscriptionPlans';
import { IOSSubscriptionManagement } from '@/components/subscription/IOSSubscriptionManagement';
import ErrorDisplay from '@/components/subscription/ErrorDisplay';
import LoadingState from '@/components/subscription/LoadingState';
import SubscriptionFooter from '@/components/subscription/SubscriptionFooter';
import { useReferralStatus } from '@/hooks/affiliates/useReferralStatus';

/**
 * Enhanced Subscription Page
 *
 * PHASE 3.1: Paywall Enhancement with iOS IAP Integration
 *
 * This page provides a unified subscription interface that automatically
 * adapts based on the user's platform through the PaymentRoutingService:
 *
 * - iOS Native App: Routes to iOS IAP through App Store integration
 * - Android/Web: Routes to existing Stripe payment interface
 *
 * Features:
 * - Automatic platform detection and payment routing
 * - Enhanced error handling for iOS IAP scenarios
 * - Unified subscription management across platforms
 * - Seamless integration with existing permissions system
 */
const Subscription = () => {
  const {
    isPremium,
    permissionsStatus,
    refreshPermissions,
    error,
    isRefreshing,
    initiateSubscription
  } = usePermissions();
  
  const [selectedPeriod, setSelectedPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [pageError, setPageError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const { isApplied } = useReferralStatus(selectedPeriod);

  // Help content is now handled by MainLayout via headerUtils

  // Run subscription check on mount but only after permissions are ready
  useEffect(() => {
    if (permissionsStatus === 'loading') {

      return;
    }
    
    try {

      refreshPermissions();
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
      setPageError('Could not check subscription status. Please refresh the page or try again later.');
    }
  }, [refreshPermissions, permissionsStatus]);

  // Show a toast notification when a referral is detected
  useEffect(() => {
    if (isApplied && permissionsStatus === 'ready') {
      toast({
        title: "Referral discount ready",
        description: "Your referral discount will be applied at checkout.",
      });
    }
  }, [isApplied, permissionsStatus]);

  const handleRefresh = async () => {
    try {
      setPageError(null);
      await refreshPermissions();
    } catch (error) {
      console.error('Error refreshing status:', error);
      setPageError('Failed to refresh subscription status');
    }
  };

  const handleSelectPlan = async (_priceId: string) => {
    if (permissionsStatus === 'loading') {
      toast({
        title: 'Please wait',
        description: 'Your account is still being loaded. Please try again in a moment.',
        variant: 'default'
      });
      return;
    }

    const isIOSNative = isIOSNativeApp();

    try {
      // Determine the price type based on the selected billing period
      const priceType = selectedPeriod === 'monthly' ? 'monthly' : 'annual';

      console.log('🛒 Subscription Page: Initiating subscription:', {
        priceType,
        platform: isIOSNative ? 'iOS Native' : 'Web/Android',
        selectedPeriod
      });

      // Use the initiateSubscription function from permissions context
      // This automatically routes through PaymentRoutingService based on platform
      await initiateSubscription(priceType);

      // Show platform-specific success message
      if (isIOSNative) {
        console.log('📱 Subscription Page: iOS IAP initiated successfully');
        // iOS IAP success is handled by the SubscriptionPlans component
        // No additional toast needed here as the purchase flow is different
      } else {
        console.log('💳 Subscription Page: Stripe payment initiated successfully');
        // Stripe will redirect to checkout, so no success message needed
      }

    } catch (error) {
      console.error('❌ Subscription Page: Error initiating subscription:', error);

      if (isIOSNative) {
        // Use centralized iOS IAP error handling
        const classification = handleIOSIAPError(
          error instanceof Error ? error : String(error),
          'Subscription Initiation'
        );
        setPageError(classification.message);
      } else {
        // Web/Android Stripe error handling
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        let userFriendlyMessage = 'There was a problem initiating your subscription. Please try again.';
        let toastTitle = 'Subscription Error';

        if (errorMessage.includes('not authenticated')) {
          userFriendlyMessage = 'Please sign in to continue with your subscription.';
          toastTitle = 'Authentication Required';
        }

        setPageError(userFriendlyMessage);
        toast({
          title: toastTitle,
          description: userFriendlyMessage,
          variant: 'destructive'
        });
      }
    }
  };

  // Show full page loading state while permissions load
  if (permissionsStatus === 'loading') {
    return (
      <>
        {/* MainLayout handles MobileHeader, so no need for duplicate header */}
        <LoadingState />
      </>
    );
  }

  // Show error state
  if (permissionsStatus === 'error' || pageError) {
    return (
      <>
        {/* MainLayout handles MobileHeader, so no need for duplicate header */}
        <ErrorDisplay
          errorMessage={pageError || error || null}
          onRetry={handleRefresh}
          isRetrying={isRefreshing}
        />
      </>
    );
  }

  return (
    <>
      {/* MainLayout handles MobileHeader, so no need for duplicate header */}
      <div className="container max-w-6xl py-8 space-y-8">
        {!isMobile && <SubscriptionHeader />}
        
        <BillingPeriodSelector 
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
        />

        <SubscriptionPlans
          billingPeriod={selectedPeriod}
          onSelectPlan={handleSelectPlan}
          isLoading={isRefreshing}
        />

        {/* iOS-specific subscription management for premium users */}
        {isPremium && (
          <IOSSubscriptionManagement
            isVisible={true}
            className="max-w-md mx-auto"
          />
        )}

        <SubscriptionFooter />
      </div>
    </>
  );
};

export default Subscription;
